<template>
  <b-container class="signup-form boxed text-left">
    <b-row class="form-row" align-h="center">
      <b-col>

        <h2 class="form-title">Formulario de Inscripción</h2>
        <p>Los campos obligatorios están identificados con un <b>(*)</b></p>

        <form v-if="!this.formSent">
          <div class="inner-form-row row card-like">

            <div class="section-title col-12">
              <h3>Datos Personales</h3>
              <small>De la persona que hace el registro del comercio y/o servicio</small>
            </div>

            <b-form-group
              :class="`${formGroupClasses}`"
              :state="!$v.person.name.$error"
              :invalid-feedback="`Este campo es requerido y debe tener un mínimo de ${$v.person.name.$params.minLength.min} caracteres`"
            >
              <label for="nameInput">Nombre (*)</label>
              <b-form-input id="nameInput"
                            type="text"
                            class="form-control"
                            :state="!$v.person.name.$anyDirty ? null : !$v.person.name.$error"
                            v-model.trim="$v.person.name.$model"
                            placeholder="Nombres y Apellidos">
              </b-form-input>
              <small class="form-text text-muted">{{ privateInfoLabelText }}</small>
            </b-form-group>

            <b-form-group
              :class="`${formGroupClasses}`"
              :state="!$v.person.nid.$error"
              :invalid-feedback="`Este campo es requerido y debe tener un mínimo de ${$v.person.nid.$params.minLength.min} caracteres`"
            >
              <label for="nidInput">Cedula (*)</label>
              <b-form-input id="nidInput"
                            type="text"
                            class="form-control"
                            :state="!$v.person.nid.$anyDirty ? null : !$v.person.nid.$error"
                            v-model.trim="$v.person.nid.$model"
                            placeholder="Cédula de identidad">
              </b-form-input>
              <small class="form-text text-muted">{{ privateInfoLabelText }}</small>
            </b-form-group>

            <b-form-group
              :class="`${formGroupClasses}`"
              :state="!$v.person.birthdate.$error"
              :invalid-feedback="`Este campo es requerido, y debes ser mayor de edad`"
            >
              <label for="birthdateInput">Fecha de nacimiento (*)</label>

              <date-dropdown
                id="birthdateInput-wrapper"
                v-model="$v.person.birthdate.$model"

                :months-names="this.formConfig.formatLocale.months.join(', ')"

                :class="`w-100 ${$v.person.birthdate.$error ? ' is-invalid' : ''}`"

                :min="this.minBirthdate()"
                :max="this.maxBirthdate()"

              ></date-dropdown>
              <small class="form-text text-muted">{{ privateInfoLabelText }}</small>
            </b-form-group>

            <b-form-group
              :class="`${formGroupClasses}`"
              :state="!$v.person.position.$error"
              :invalid-feedback="`Este campo es requerido`"
            >
              <label for="positionInput">Cargo que ocupa en comercio/servicio (*)</label>
              <b-form-input id="positionInput"
                            type="text"
                            class="form-control"
                            :state="!$v.person.position.$anyDirty ? null : !$v.person.position.$error"
                            v-model.trim="$v.person.position.$model"
                            placeholder="Cargo">
              </b-form-input>
              <small class="form-text text-muted">{{ privateInfoLabelText }}</small>
            </b-form-group>

            <b-form-group
              :class="`form-group col-12 col-sm-8`"
              :state="!$v.person.position.$error"
              :invalid-feedback="`Este campo es requerido`"
            >
              <label for="hasLegalPowerInput">
                Tiene la capacidad legal para contratar en nombre del comercio y/o servicio? (*)
              </label>
              <b-form-select id="hasLegalPowerInput"
                             class="custom-select mr-sm-2"
                             v-model.trim="$v.person.hasLegalPower.$model"
                             :state="!$v.person.hasLegalPower.$anyDirty ? null : !$v.person.hasLegalPower.$error"
              >
                <option selected value="null">Seleccione una opción</option>
                <option value="0">No</option>
                <option value="1">Si</option>
              </b-form-select>
              <small class="form-text text-muted">{{ privateInfoLabelText }}</small>
            </b-form-group>

            <b-form-group :class="`${formGroupClasses}`"
                          :state="!$v.person.phone1.$error"
                          :invalid-feedback="`Teléfono inválido - Este campo es requerido`"
            >
              <label for="phone1Input">Telefono 1 (*)</label>
              <the-mask
                :id="`phone1Input`"
                :placeholder="`(XXXX) XXX-XXXX`"
                v-model.trim="$v.person.phone1.$model"

                :class="`form-control` + ($v.person.phone1.$error ? ' is-invalid' : (!$v.person.phone1.$invalid ? ' is-valid' : ''))"
                :mask="['(####) ###-####']" />
              <small class="form-text text-muted">{{ privateInfoLabelText }}</small>
            </b-form-group>

            <b-form-group :class="`${formGroupClasses}`"
                          :state="!$v.person.phone2.$error"
                          :invalid-feedback="`Teléfono inválido`"
            >
              <label for="phone2Input">Telefono 2</label>
              <the-mask
                :id="`phone2Input`"
                :placeholder="`(XXXX) XXX-XXXX`"
                v-model.trim="$v.person.phone2.$model"

                :class="`form-control`"
                :mask="['(####) ###-####']" />
              <small class="form-text text-muted">{{ privateInfoLabelText }}</small>
            </b-form-group>

            <b-form-group :class="`${formGroupClasses}`"
                          :state="!$v.person.phone3.$error"
                          :invalid-feedback="`Teléfono inválido`"
            >
              <label for="phone3Input">Telefono 3</label>
              <the-mask
                :id="`phone3Input`"
                :placeholder="`(XXXX) XXX-XXXX`"
                v-model.trim="$v.person.phone3.$model"

                :class="`form-control`"
                :mask="['(####) ###-####']" />
              <small class="form-text text-muted">{{ privateInfoLabelText }}</small>
            </b-form-group>

            <b-form-group
              :class="`form-group col-12`"
              :state="!$v.person.email.$error"
              :invalid-feedback="`Correo Inválido - Este campo es requerido`"
            >
              <label for="emailInput">Correo electrónico</label>
              <b-form-input id="emailInput"
                            type="email"
                            class="form-control"
                            :state="!$v.person.email.$anyDirty ? null : !$v.person.email.$error"
                            v-model.trim="$v.person.email.$model"
                            placeholder="ejemplo@dominio.com">
              </b-form-input>
              <small class="form-text text-muted">{{ privateInfoLabelText }}</small>
            </b-form-group>

            <div :class="`form-group col-12 col-sm-12 form-group-check`">
              <input id="newsletterCheckInput"
                     class="col-1 form-check-input"
                     type="checkbox"
                     v-model="person.acceptEmailComms">
              <label class="col-11 form-check-label" for="newsletterCheckInput">
                Deseas recibir en tu correo electrónico información sobre cambios en el Acuerdo sobre
                Términos y Condiciones cuando se generen, así como información de novedades sobre la plataforma?
              </label>
            </div>

          </div>

          <!-- BUSINESS SECTION START -->

          <div class="inner-form-row row mt-3 card-like">

            <div class="section-title col-12">
              <h3>Información del Comercio y/o Servicio</h3>
            </div>

            <b-form-group
              :class="`form-group col-12 col-sm-8`"
              :state="!$v.business.name.$error"
              :invalid-feedback="`Este campo es requerido`"
            >
              <label for="businessNameInput">Nombre del Comercio (*)</label>
              <b-input type="text" class="form-control" id="businessNameInput"
                       v-model.trim="$v.business.name.$model"
                       :state="!$v.business.name.$anyDirty ? null : !$v.business.name.$error"
                       placeholder="Nombre del comercio y/o servicio"></b-input>
              <small class="form-text text-muted">{{ publicInfoLabelText }}</small>
            </b-form-group>

            <b-form-group
              :class="`${formGroupClasses}`"
              :state="!$v.business.taxId.$error"
              :invalid-feedback="`RIF Inválido - Este campo es requerido`"
            >
              <label for="businessTidInput">RIF (*)</label>
              <the-mask
                :id="`locationPhone1InbusinessTidInput`"
                :placeholder="`RIF de la Empresa`"
                v-model.trim="$v.business.taxId.$model"

                :class="`form-control` + ($v.business.taxId.$error ? ' is-invalid' : (!$v.business.taxId.$invalid ? ' is-valid' : ''))"
                :mask="['A-#########-#', 'A-########-#', 'A-#######-#']" />
              <small class="form-text text-muted">{{ privateInfoLabelText }}</small>
            </b-form-group>

            <b-form-group
              :class="`form-group col-12`"
              :state="!$v.business.slogan.$error"
              :invalid-feedback="`Este campo es requerido y debe tener un mínimo de ${$v.business.description.$params.minLength.min} caracteres`"
            >
              <label for="businessSloganInput">Eslogan</label>
              <b-input type="text" class="form-control" id="businessSloganInput"
                       v-model.trim="$v.business.slogan.$model"
                       :state="!$v.business.slogan.$anyDirty ? null : !$v.business.slogan.$error"
                       placeholder="Eslogan del comercio y/o servicio"></b-input>
              <small class="form-text text-muted">{{ publicInfoLabelText }}</small>
            </b-form-group>

            <b-form-group
              :class="`form-group col-12`"
              :state="!$v.business.description.$error"
              :invalid-feedback="`Este campo es requerido y debe tener un mínimo de ${$v.business.description.$params.minLength.min} caracteres`"
            >
              <label for="businessDescriptionInput">Productos o servicios ofrecidos (*)</label>
              <b-form-textarea
                id="businessDescriptionInput"
                type="text"
                class="form-control"
                v-model.trim="$v.business.description.$model"
                :state="!$v.business.description.$anyDirty ? null : !$v.business.description.$error"
                placeholder="Descripción breve de productos o servicios ofrecidos">
              </b-form-textarea>
              <small class="form-text text-muted">{{ publicInfoLabelText }} - {{ 150 - business.description.length }} caracteres restantes</small>
            </b-form-group>

            <b-form-group
              :class="`form-group col-12 col-sm-8`"
              :state="!$v.business.categories.$error"
              :invalid-feedback="`Debes seleccionar al menos una Categoría`"
            >
              <b-form-group label="Categoria(s) a la(s) que pertenece tu negocio/servicio (*)">
                <b-form-checkbox
                  v-for="option in this.$store.state.staticCategories"
                  v-model="$v.business.categories.$model"
                  :key="option.id"
                  :value="option.id"
                  :switch="true"

                  :state="$v.business.categories.$anyDirty && $v.business.categories.$error ? false : null"
                >
                  {{ option.name }}
                </b-form-checkbox>
              </b-form-group>
            </b-form-group>

            <b-form-group
              :class="`form-group col-12 col-sm-4`"
              :state="!$v.business.features.$error"
              :invalid-feedback="`Debes seleccionar al menos una Facilidad`"
            >
              <b-form-group label="Facilidades (*)">
                <b-form-checkbox
                  v-for="option in this.$store.state.staticPlaceFeatures"
                  v-model="$v.business.features.$model"
                  :key="option.id"
                  :value="option.id"
                  :switch="true"

                  :state="$v.business.features.$anyDirty && $v.business.features.$error ? false : null"
                >
                  {{ option.name }}
                </b-form-checkbox>
              </b-form-group>
            </b-form-group>


            <div class="form-location-section row w-100">

              <div class="col-12">
                <h3>Ubicación</h3>
              </div>

              <div class="col-12 col-sm-8">
                <div class="container">

                  <b-form-group
                    :class="`form-group col-12`"
                    :state="!$v.business.location.state.$error"
                    :invalid-feedback="`Este campo es requerido`"
                  >
                    <label for="stateInput">Estado (*)</label>
                    <b-form-select
                      id="stateInput"
                      :options="this.$store.state.staticStates"

                      v-model.trim="$v.business.location.state.$model"
                      :state="!$v.business.location.state.$anyDirty ? null : !$v.business.location.state.$error"
                    ></b-form-select>
                    <small class="form-text text-muted">{{ privateInfoLabelText }}</small>
                  </b-form-group>


                  <!-- FIXME -->
                  <div :class="`form-group col-12`">
                    <label for="cityInput">Ciudad (*)</label>
                    <b-form-select
                      id="cityInput"
                      :disabled="!business.location.state"
                      :options="business.location.state ? this.$store.getters.getCities(business.location.state.id) : []"

                      v-model.trim="$v.business.location.city.$model"
                      :state="!$v.business.location.city.$anyDirty ? null : !$v.business.location.city.$error"
                    ></b-form-select>
                    <small class="form-text text-muted">{{ privateInfoLabelText }}</small>
                  </div>

                  <b-form-group
                    :class="`form-group col-12`"
                    :state="!$v.business.location.address.$error"
                    :invalid-feedback="`Este campo es requerido, al menos que selecciones que no deseas incluir una dirección`"
                  >
                    <label for="addressInput">Dirección (*)</label>
                    <b-form-input
                      id="addressInput"
                      type="text"
                      class="form-control"

                      v-model.trim="$v.business.location.address.$model"
                      :state="!$v.business.location.address.$anyDirty ? null : !$v.business.location.address.$error"

                      :disabled="business.location.noStreetAddress"
                      placeholder="Dirección"></b-form-input>
                    <b-checkbox
                      id="noStreetAddressInput"
                      v-model="business.location.noStreetAddress"
                      switch>
                      No deseo incluir una dirección
                    </b-checkbox>
                    <small class="form-text text-muted">{{ privateInfoLabelText }}</small>
                  </b-form-group>
                </div>
              </div>


              <div class="col-12 col-sm-4 pb-3">
<!--                <l-map-->
<!--                  id="map-preview"-->
<!--                  ref="myMap"-->
<!--                  :zoom="mapProps.zoom"-->
<!--                  :center="mapProps.center"-->
<!--                  :options="mapProps.options"-->
<!--                  :dragging="`false`"-->
<!--                  @update:center="mapCenterUpdate"-->
<!--                  @update:zoom="mapZoomUpdate"-->
<!--                >-->
<!--                  <l-tile-layer-->
<!--                    :url="mapProps.tileUrl"-->
<!--                    :attribution="mapProps.attribution"-->
<!--                  />-->
<!--                  <l-marker :lat-lng="mapProps.center" :icon="mapProps.markerIcon"></l-marker>-->
<!--                </l-map>-->
              </div>

            </div>

            <div class="form-location-section row w-100">

              <div class="col-12">
                <h3>Horarios de Apertura</h3>
              </div>

              <b-form-group
                :class="`form-group col-12 col-sm-4`"
                :state="!$v.business.workingHours.monToFri.$error"
                :invalid-feedback="`Este campo es requerido`"
              >
                <label for="monToFriHoursInput">Lunes a Viernes</label>
                <b-form-input
                  id="monToFriHoursInput" type="text"
                  placeholder="Ej: 8:00 am a 12:00 m - 2:00 pm a 5:30 pm"

                  v-model.trim="$v.business.workingHours.monToFri.$model"
                  :state="!$v.business.workingHours.monToFri.$anyDirty ? null : !$v.business.workingHours.monToFri.$error"

                  :disabled="!business.location.opensMonToFri"
                ></b-form-input>
                <b-checkbox
                  id="opensMonToFri"
                  v-model="business.location.opensMonToFri"
                  switch>
                  Abierto
                </b-checkbox>
                <small class="form-text text-muted">{{ publicInfoLabelText }}</small>
              </b-form-group>

              <b-form-group
                :class="`form-group col-12 col-sm-4`"
                :state="!$v.business.workingHours.sat.$error"
                :invalid-feedback="`Este campo es requerido`"
              >
                <label for="satHoursInput">Sábado</label>
                <b-form-input
                  id="satHoursInput" type="text"
                  placeholder="Ej: 8:00 am a 12:00 m - 2:00 pm a 5:30 pm"

                  v-model.trim="$v.business.workingHours.sat.$model"
                  :state="!$v.business.workingHours.sat.$anyDirty ? null : !$v.business.workingHours.sat.$error"

                  :disabled="!business.location.opensSat"
                ></b-form-input>
                <b-checkbox
                  id="opensSat"
                  v-model="business.location.opensSat"
                  switch>
                  Abierto
                </b-checkbox>
                <small class="form-text text-muted">{{ publicInfoLabelText }}</small>
              </b-form-group>

              <b-form-group
                :class="`form-group col-12 col-sm-4`"
                :state="!$v.business.workingHours.sun.$error"
                :invalid-feedback="`Este campo es requerido`"
              >
                <label for="sunHoursInput">Domingos</label>
                <b-form-input
                  id="sunHoursInput" type="text"
                  placeholder="Ej: 8:00 am a 12:00 m - 2:00 pm a 5:30 pm"

                  v-model.trim="$v.business.workingHours.sun.$model"
                  :state="!$v.business.workingHours.sun.$anyDirty ? null : !$v.business.workingHours.sun.$error"

                  :disabled="!business.location.opensSun"
                ></b-form-input>
                <b-checkbox
                  id="opensSun"
                  v-model="business.location.opensSun"
                  switch>
                  Abierto
                </b-checkbox>
                <small class="form-text text-muted">{{ publicInfoLabelText }}</small>
              </b-form-group>

              <b-form-group
                :class="`${formGroupClasses}`"
                :state="!$v.person.phone1.$error"
                :invalid-feedback="`Teléfono inválido - Este campo es requerido`"
              >
                <label for="locationPhone1Input">Telefono Contacto 1 (*)</label>
                <the-mask
                  :id="`locationPhone1Input`"
                  :placeholder="`(XXXX) XXX-XXXX`"
                  v-model.trim="$v.business.phone1.$model"

                  :class="`form-control` + ($v.business.phone1.$error ? ' is-invalid' : (!$v.business.phone1.$invalid ? ' is-valid' : ''))"
                  :mask="['(####) ###-####']" />
                <small class="form-text text-muted">{{ privateInfoLabelText }}</small>
              </b-form-group>

              <b-form-group
                :class="`${formGroupClasses}`"
                :state="!$v.business.phone2.$error"
                :invalid-feedback="`Teléfono inválido`"
              >
                <label for="locationPhone2Input">Telefono Contacto 2</label>
                <the-mask
                  :id="`locationPhone2Input`"
                  :placeholder="`(XXXX) XXX-XXXX`"
                  v-model.trim="$v.business.phone2.$model"

                  :class="`form-control`"
                  :mask="['(####) ###-####']" />
                <small class="form-text text-muted">{{ privateInfoLabelText }}</small>
              </b-form-group>

              <b-form-group
                :class="`${formGroupClasses}`"
                :state="!$v.business.phone3.$error"
                :invalid-feedback="`Teléfono inválido`"
              >
                <label for="locationPhone3Input">Telefono Contacto 3</label>
                <the-mask
                  :id="`locationPhone3Input`"
                  :placeholder="`(XXXX) XXX-XXXX`"
                  v-model.trim="$v.business.phone3.$model"

                  :class="`form-control`"
                  :mask="['(####) ###-####']" />
                <small class="form-text text-muted">{{ privateInfoLabelText }}</small>
              </b-form-group>


              <div :class="`form-group col-12`">
                <b-form-checkbox switch size="md"
                                 v-model="business.phoneHoursDiffFromStore"
                >
                  Horarios de atención telefónica diferentes a los de apertura
                </b-form-checkbox>
              </div>

              <b-form-group
                class="form-group col-12 col-sm-4"
                v-if="business.phoneHoursDiffFromStore"
                :state="!$v.business.phoneHours.monToFri.$error"
                :invalid-feedback="`Horario Inválido - Este campo es requerido`"
              >
                <label for="monToFriPhoneHoursInput">
                  <i class="fa fa-phone" aria-hidden="true"></i>
                  Lunes a Viernes
                </label>
                <b-form-input
                  id="monToFriPhoneHoursInput" type="text"
                  placeholder="Ej: 8:00 am a 12:00 m - 2:00 pm a 5:30 pm"

                  v-model.trim="$v.business.phoneHours.monToFri.$model"
                  :state="!$v.business.phoneHours.monToFri.$anyDirty ? null : !$v.business.phoneHours.monToFri.$error"
                ></b-form-input>
              </b-form-group>

              <b-form-group
                :class="`form-group col-12 col-sm-4`"
                v-if="business.phoneHoursDiffFromStore"
                :state="!$v.business.phoneHours.sat.$error"
                :invalid-feedback="`Horario Inválido - Este campo es requerido`"
              >
                <label for="satPhoneHoursInput">
                  <i class="fa fa-phone" aria-hidden="true"></i>
                  Sabados
                </label>
                <b-form-input
                  id="satPhoneHoursInput" type="text"
                  placeholder="Ej: 8:00 am a 12:00 m - 2:00 pm a 5:30 pm"

                  v-model.trim="$v.business.phoneHours.sat.$model"
                  :state="!$v.business.phoneHours.sat.$anyDirty ? null : !$v.business.phoneHours.sat.$error"
                ></b-form-input>
              </b-form-group>

              <b-form-group
                :class="`form-group col-12 col-sm-4`"
                v-if="business.phoneHoursDiffFromStore"
                :state="!$v.business.phoneHours.sun.$error"
                :invalid-feedback="`Horario Inválido - Este campo es requerido`"
              >
                <label for="sunPhoneHoursInput">
                  <i class="fa fa-phone" aria-hidden="true"></i>
                  Sabados
                </label>
                <b-form-input
                  id="sunPhoneHoursInput" type="text"
                  placeholder="Ej: 8:00 am a 12:00 m - 2:00 pm a 5:30 pm"

                  v-model.trim="$v.business.phoneHours.sun.$model"
                  :state="!$v.business.phoneHours.sun.$anyDirty ? null : !$v.business.phoneHours.sun.$error"
                ></b-form-input>
              </b-form-group>

            </div>


            <div class="col-12">
              <h3>Que tus clientes te contacten al Whatsapp de tu negocio</h3>
            </div>
            <b-form-group
              class="form-group col-12"
              :state="!$v.business.whatsappPhone.$error"
              :invalid-feedback="`Teléfono inválido`"
            >
              <label for="whatsappPhone1Input">
                <i class="fa fa-whatsapp" aria-hidden="true"></i>
                Número de <b>Whatsapp de tu negocio</b>
              </label>
              <the-mask
                :id="`whatsappPhone1Input`"
                :placeholder="`(XXXX) XXX-XXXX`"

                v-model.trim="$v.business.whatsappPhone.$model"

                :class="`form-control`"
                :mask="['(####) ###-####']" />
              <small class="form-text text-muted">{{ publicInfoLabelText }}</small>
            </b-form-group>

            <div class="col-12">
              <h3>Redes Sociales</h3>
            </div>

            <b-form-group
              :class="`${formGroupClasses}`"
              :state="!$v.business.fbUrl.$error"
              :invalid-feedback="`URL Inválida`"
            >
              <label for="fbInput">
                <i class="fa fa-facebook-official" aria-hidden="true"></i>
                Facebook
              </label>
              <b-form-input
                id="fbInput"
                placeholder="URL de página de Facebook"
                :formatter="urlFormatter"

                v-model.trim="$v.business.fbUrl.$model"
                :state="!$v.business.fbUrl.$anyDirty ? null : !$v.business.fbUrl.$error"
              ></b-form-input>
              <small class="form-text text-muted">{{ publicInfoLabelText }}</small>
            </b-form-group>


            <b-form-group
              :class="`${formGroupClasses}`"
              :state="!$v.business.igUser.$error"
              :invalid-feedback="`Usuario Inválido`"
            >
              <label for="igInput">
                <i class="fa fa-instagram" aria-hidden="true"></i>
                Instagram
              </label>
              <b-input-group prepend="@" class="mb-2 mr-sm-2 mb-sm-0">
                <b-form-input
                  id="igInput"
                  placeholder="Perfil de Instagram"
                  :formatter="socialUserFormatter"

                  v-model.trim="$v.business.igUser.$model"
                  :state="!$v.business.igUser.$anyDirty ? null : !$v.business.igUser.$error"
                ></b-form-input>
              </b-input-group>
              <small class="form-text text-muted">{{ publicInfoLabelText }}</small>
            </b-form-group>

            <b-form-group
              :class="`${formGroupClasses}`"
              :state="!$v.business.twUser.$error"
              :invalid-feedback="`Usuario Inválido`"
            >
              <label for="twInput">
                <i class="fa fa-twitter" aria-hidden="true"></i>
                Twitter
              </label>
              <b-input-group prepend="@" class="mb-2 mr-sm-2 mb-sm-0">
                <b-form-input
                  id="twInput"
                  placeholder="Perfil de Twitter"
                  :formatter="socialUserFormatter"

                  v-model.trim="$v.business.twUser.$model"
                  :state="!$v.business.twUser.$anyDirty ? null : !$v.business.twUser.$error"
                ></b-form-input>
              </b-input-group>
              <small class="form-text text-muted">{{ publicInfoLabelText }}</small>
            </b-form-group>

            <b-form-group
              class="form-group col-12 col-sm-6"
              :state="!$v.business.websiteUrl.$error"
              :invalid-feedback="`URL Inválida`"
            >
              <label for="webInput">
                Página Web
              </label>
              <b-form-input
                id="webInput"
                placeholder="URL de tu sitio Web. Ej: tunegocio.com"
                :formatter="urlFormatter"

                v-model.trim="$v.business.websiteUrl.$model"
                :state="!$v.business.websiteUrl.$anyDirty ? null : !$v.business.websiteUrl.$error"
              ></b-form-input>
              <small class="form-text text-muted">{{ publicInfoLabelText }}</small>
            </b-form-group>

            <b-form-group
              class="form-group col-12 col-sm-6"
              :state="!$v.business.email.$error"
              :invalid-feedback="`Correo Inválido`"
            >
              <label for="emailInput">
                Correo Electrónico
              </label>
              <b-form-input
                id="emailInput"
                placeholder="Tu dirección de email"
                :formatter="emailFormatter"

                v-model.trim="$v.business.email.$model"
                :state="!$v.business.email.$anyDirty ? null : !$v.business.email.$error"
              ></b-form-input>
              <small class="form-text text-muted">{{ publicInfoLabelText }}</small>
            </b-form-group>


            <div class="col-12">
              <a class="button-anchor" v-b-modal.modal-legal>Haga click aquí para ver los Términos y Condiciones de BuscameAqui.com</a>

              <b-modal id="modal-legal" scrollable title="Términos y Condiciones">
                <LegalContents />
              </b-modal>

              <b-form-checkbox
                v-model="$v.acceptTermsAndConditions.$model"
                name="acceptTerms"
                value="1"
                unchecked-value="0"

                :state="(!$v.acceptTermsAndConditions.$anyDirty || acceptTermsAndConditions == 1) ? null : false"
              >
                Declaro que he leido y acepto el acuerdo de Términos y Condiciones de BuscameAquí.com
              </b-form-checkbox>

            </div>

            <div v-if="sendError === true" class="text-center w-100">
              <small
                class="form-text text-red">
                Ocurrió un error al enviar los datos, por favor intenta nuevamente.
              </small>
            </div>

            <div class="col-12 btn-submit-container text-center">
              <b-button v-on:click="onSubmitButtonClick" :variant="`primary`" >Enviar datos para Registro</b-button>
            </div>

          </div>

        </form>

        <div v-else class="row card-like">
          <b-col cols="12" class="message-sent p-4">
            <p class="message-title">¡Tus datos fueron enviados!</p>
            <p>
              Pronto nos pondremos en contacto contigo para hacer la validación
              de los mismos y finalizar el registo en la plataforma de
              <span class="site-name">BUSCAMEAQUI.COM</span>
            </p>
            <div class="col-12 btn-submit-container text-center">
              <b-button  :variant="`primary`" @click="navigateHome" >Volver al Inicio</b-button>
            </div>

          </b-col>
        </div>

      </b-col>
    </b-row>

    <loading :active.sync="this.$store.state.registerFormSubmitting"
             :can-cancel="false"
             :is-full-page="true"
             color="#f15b2b"
             background-color="#8a8a8a"
             loader="dots"
    ></loading>

  </b-container>
</template>

<script>
  import JQuery from 'jquery'

  import Loading from 'vue-loading-overlay';

  import { validationMixin } from 'vuelidate'
  import { required, minLength, maxLength, email, requiredIf } from "vuelidate/lib/validators";

  import DateDropdown from 'vue-date-dropdown'
  import { TheMask } from 'vue-the-mask'
  import L from 'leaflet';
  import { latLng } from "leaflet";
  // import { LMap, LTileLayer, LMarker } from 'vue2-leaflet';
  import LegalContents from "@/components/LegalContents.vue";
  import moment from 'moment';

  // Custom validator for multi-select
  const mustSelectAtLeastOne = (arr) => arr.length > 0
  const isValidUrl = (url) => {
    const res = url.match(/(http(s)?:\/\/.)?(www\.)?[-a-zA-Z0-9@:%._+~#=]{2,256}\.[a-z]{2,6}\b([-a-zA-Z0-9@:%_+.~#?&//=]*)/g);
    return url === "" || res != null;
  }

  const isValidFacebookUrl = (url) => {
    return url === "" || (isValidUrl(url) && url.match(/facebook\.com\//) !== null)
  }

  const isValidBirthdate = (birthdate) => {
    console.log(`Birthdate is: ${birthdate}`)
    if (birthdate.match(/^[0-9]+\.[0-9]+\.[0-9]+$/) != null) {
      const bDate = moment(birthdate, "DD.MM.YYYY")
      console.log(`diff in years: ${moment().diff(bDate, 'years')}`)
      return moment().diff(bDate, 'years') >= 18
    } else {
      return false;
    }
  }

  export default {
    name: 'BusinessRegisterForm',
    components: {
      Loading,
      DateDropdown,
      TheMask,
      // LMap, LTileLayer, LMarker,
      LegalContents
    },
    mixins: [validationMixin],

    props: [
      'privateInfoLabelText',
      'publicInfoLabelText'
    ],

    data() {
      return {

        formGroupClasses: "form-group col-12 col-sm-4",

        formSent: false,

        formConfig: {
          formatLocale: {
            // MMMM
            months: [
              'Enero', 'Febrero', 'Marzo', 'Abril',
              'Mayo', 'Junio', 'Julio', 'Agosto',
              'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
            ],
            // MMM
            monthsShort: ['Ene', 'Feb', 'Mar', 'Abr',
              'May', 'Jun', 'Jul', 'Agos',
              'Sep', 'Oct', 'Nov', 'Dic'
            ],
            // dddd
            weekdays: ['Domingo', 'Lunes', 'Martes', 'Mircoles', 'Jueves', 'Viernes', 'Sábado'],
            // ddd
            weekdaysShort: ['Dom', 'Lun', 'Mar', 'Mie', 'Jue', 'Vie', 'Sab'],
            // dd
            weekdaysMin: ['Do', 'Lu', 'Ma', 'Mi', 'Ju', 'Vi', 'Sa'],
            // first day of week
            firstDayOfWeek: 1,
          },
        },

        person: {
          name: "",
          nid: "",
          birthdate: null,
          position: "",
          hasLegalPower: null,
          phone1: "",
          phone2: "",
          phone3: "",
          email: "",
          acceptEmailComms: true
        },

        business: {
          name: "",
          taxId: "",
          slogan: "",
          description: "",
          categories: [],
          features: [1],
          location: {
            state: null,
            city: null,
            address: "",
            noStreetAddress: false,
            lat: null,
            lon: null,

            opensMonToFri: true,
            opensSat: true,
            opensSun: true
          },
          workingHours: {
            monToFri: "",
            sat: "",
            sun: ""
          },
          phone1: "",
          phone2: "",
          phone3: "",
          phoneHoursDiffFromStore: false,
          phoneHours: {
            monToFri: "",
            sat: "",
            sun: ""
          },
          whatsappPhone: "",
          fbUrl: "",
          igUser: "",
          twUser: "",
          websiteUrl: "",
          email: ""
        },

        acceptTermsAndConditions: false,

        mapProps: {
          tileUrl: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
          attribution:
            '&copy; <a href="http://osm.org/copyright">OpenStreetMap</a> contributors',

          zoom: 5,
          center: latLng(7.819847426192587, -66.99679684371664),
          options: {
            zoomSnap: 0.5
          },
          currentZoom: 5,
          currentCenter: latLng(7.819847426192587, -66.99679684371664),
          markerIcon: L.icon({
            iconUrl: 'http://leafletjs.com/examples/custom-icons/leaf-green.png',
            shadowUrl: 'http://leafletjs.com/examples/custom-icons/leaf-shadow.png',
            iconSize: [38, 95],
            shadowSize: [50, 64],
            iconAnchor: [22, 94],
            shadowAnchor: [4, 62],
            popupAnchor: [-3, -76]
          })
        }
      }
    },

    watch: {
      '$store.state.registerFormSubmitting': function (val) {
        if (val === false) {
          if (this.$store.state.registerFormSubmitResult &&
            this.$store.state.registerFormSubmitResult.success === true) {
            console.log("Register form sucessfully sent!");

            this.onFormSubmitSuccess();

          } else if (this.$store.state.registerFormSubmitResult &&
            this.$store.state.registerFormSubmitResult.success === false) {
            console.log("Register form send error")
            console.log(this.$store.state.registerFormSubmitResult.error)
            // TODO: Show error message
          }
        }
      },
      'business.location.opensMonToFri': function (val) {
        this.business.workingHours.monToFri = (val === true ? "" : "Cerrado")
      },
      'business.location.opensSat': function (val) {
        this.business.workingHours.sat = (val === true ? "" : "Cerrado")
      },
      'business.location.opensSun': function (val) {
        this.business.workingHours.sun = (val === true ? "" : "Cerrado")
      }
    },

    computed: {
      addressValidations() {
        if (!this.business.location.noStreetAddress) {
          return { required }
        } else {
          return {}
        }
      },

      phoneHoursValidations() {
        if (this.business.phoneHoursDiffFromStore) {
          return {
            required,
            minLength: minLength(5)
          }
        } else {
          return {}
        }
      },

      sendError: function () {
        return this.$store.state.registerFormSubmitResult !== null
          && this.$store.state.registerFormSubmitResult.success === false;
      }
    },

    validations() {
      return {
        person: {
          name: {
            required,
            minLength: minLength(4)
          },
          nid: {
            required,
            minLength: minLength(7)
          },
          birthdate: {
            required,
            isValidBirthdate
          },
          position: {required},
          hasLegalPower: {required},
          phone1: {
            required,
            minLength: minLength(11)
          },
          phone2: {
            minLength: minLength(11)
          },
          phone3: {
            minLength: minLength(11)
          },
          email: {
            required,
            email
          }
        },
        business: {
          name: {
            required,
            minLength: minLength(3)
          },
          taxId: {
            required,
            minLength: minLength(9)
          },
          slogan: {
            minLength: minLength(3),
            maxLength: maxLength(50)
          },
          description: {
            required,
            minLength: minLength(16)
          },
          categories: {
            mustSelectAtLeastOne
          },
          features: {
            mustSelectAtLeastOne
          },
          location: {
            state: {required},
            city: {required},
            address: this.addressValidations
          },
          workingHours: {
            monToFri: {
              required,
              minLength: minLength(5)
            },
            sat: {
              required,
              minLength: minLength(5)
            },
            sun: {
              required,
              minLength: minLength(5)
            }
          },
          phone1: {
            required,
            minLength: minLength(11)
          },
          phone2: {
            minLength: minLength(11)
          },
          phone3: {
            minLength: minLength(11)
          },
          phoneHoursDiffFromStore: false,
          phoneHours: {
            monToFri: this.phoneHoursValidations,
            sat: this.phoneHoursValidations,
            sun: this.phoneHoursValidations
          },
          whatsappPhone: {
            minLength: minLength(11)
          },
          fbUrl: {
            minLength: minLength(3),
            isValidFacebookUrl,
          },
          igUser: {
            minLength: minLength(3)
          },
          twUser: {
            minLength: minLength(3)
          },
          websiteUrl: {
            isValidUrl,
            minLength: minLength(3)
          },
          email: {
            email,
            minLength: minLength(7)
          }
        },
        acceptTermsAndConditions: {
          required,

        }
      }
    },

    methods: {
      mapZoomUpdate: function(zoom) {
        console.log(zoom)
        this.mapProps.currentZoom = zoom;
      },

      mapCenterUpdate: function(center) {
        console.log(center)
        this.mapProps.currentCenter = center;
      },

      maxBirthdate: function() {
        const eighteenYearsAgo = moment().subtract(18, "years")
        return eighteenYearsAgo.format("YYYY-MM-DD")
      },

      minBirthdate: function() {
        const oneHundredYearsAgo = moment().subtract(100, "years")
        return oneHundredYearsAgo.format("YYYY-MM-DD")
      },

      formatDate: function(date) {
        return `${date.getDay()} de ${this.formConfig.months[date.getMonth()]}, ${date.getFullYear()}`
      },

      formatBirthdate: function(dateStr) {
        return moment(dateStr, "DD.MM.YYYY").format("YYYY-MM-DD")
      },

      socialUserFormatter: function(value) {
        return value.replace(/[^A-Za-z0-9_.]/ig, "")
      },

      urlFormatter: function(value) {
        return value.replace(/[ ]/ig, "")
      },

      emailFormatter: function(value) {
        return value.replace(/[^A-Za-z0-9_.@]/ig, "")
      },

      onSubmitButtonClick: function() {
        console.log("Submit on click!", this.$v);
        this.$v.$touch();

        if (this.$v.$anyError) {
          console.log("Display Error");
          JQuery("html").scrollTop = JQuery(".is-invalid").offsetTop
        } else {
          this.doSubmit(this.person, this.business, this.acceptTermsAndConditions)
        }

      },

      doSubmit: function (personData, businessData, acceptTermsAndConditions) {
        console.log(personData, businessData, acceptTermsAndConditions)

        this.$store.dispatch({
          type: "action",
          actionType: this.$types.SUBMIT_REGISTER_FORM,
          value: {
            personData,
            businessData,
            acceptTermsAndConditions
          }
        });

      },

      onFormSubmitSuccess: function () {
        if (this.$gtag) {
          this.$gtag.event("register", {
            'event_category': "Business",
            'event_label': "register view",
            'value': 1
          });
        }

        this.formSent = true;
        this.resetFields();
      },

      resetFields: function () {
        this.person = {
          name: "",
          nid: "",
          birthdate: null,
          position: "",
          hasLegalPower: null,
          phone1: "",
          phone2: "",
          phone3: "",
          email: "",
          acceptEmailComms: true
        },

        this.business = {
          name: "",
          taxId: "",
          slogan: "",
          description: "",
          categories: [],
          features: [1],
          location: {
          state: null,
            city: null,
            address: "",
            noStreetAddress: false,
            lat: null,
            lon: null,

            opensMonToFri: true,
            opensSat: true,
            opensSun: true
          },
          workingHours: {
            monToFri: "",
              sat: "",
              sun: ""
          },
          phone1: "",
            phone2: "",
            phone3: "",
            phoneHoursDiffFromStore: false,
            phoneHours: {
            monToFri: "",
              sat: "",
              sun: ""
          },
          whatsappPhone: "",
            fbUrl: "",
            igUser: "",
            twUser: "",
            websiteUrl: "",
            email: ""
        },

        this.acceptTermsAndConditions = false

      },

      navigateHome: function () {
        this.$router.push({
          name: "home"
        });
      }

    }

};

</script>

<style scoped type="less">

  .form-group-check .form-check-input {
    margin-left: 0;
  }

  .form-group-check .form-check-label {
    margin-left: 1rem;
  }

  .inner-form-row {
    padding-top: 1rem;
    padding-bottom: 1rem;
  }

  .section-title {
    margin-bottom: 1rem;
  }

  .form-location-section {
    margin-left: 15px;
    margin-right: 15px;
    padding-top: 15px;
    margin-bottom: 15px;
    background-color: aliceblue;
  }

  #map-preview {
    min-height: 20rem;
  }

  .button-anchor {
    display: inline-block;
    color: #007bff;
    text-decoration: none;
    background-color: transparent;
    margin-top: 1rem;
    margin-bottom: 1rem;
    cursor: pointer;
  }

  .button-anchor:hover {
    color: #007bff;
    text-decoration: underline;
  }

  .btn-submit-container {
    padding-top: 1.5rem;
  }

  .message-title {
    font-size: 1.5rem;
  }

  .date-dropdown-container {
    padding: 0.25rem;
    padding-right: calc(1.5em + 0.75rem);
  }

  .date-dropdown-container.is-invalid {
    border: 1px solid #dc3545;
    border-radius: 0.25rem;

    background-position: right calc(0.375em + 0.1875rem) center;

    background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' fill='none' stroke='%23dc3545' viewBox='0 0 12 12'%3e%3ccircle cx='6' cy='6' r='4.5'/%3e%3cpath stroke-linejoin='round' d='M5.8 3.6h.4L6 6.5z'/%3e%3ccircle cx='6' cy='8.2' r='.6' fill='%23dc3545' stroke='none'/%3e%3c/svg%3e");
    background-repeat: no-repeat;
    background-size: calc(0.75em + 0.375rem) calc(0.75em + 0.375rem);
  }

</style>

